import {
  BUSINESS_UNITS,
  BusinessUnitKey,
  DEFAULT_LOGIN_ROUTE_NAME,
  getBusinessUnitConfig,
} from '@/modules/bu/registry.base';

const BU_STORAGE_KEY = 'cusp_bu';

// Resolve BU from path prefix, then domain, then optional storage.
export function getBuFromPath(path_value: string): BusinessUnitKey | undefined {
  const normalizedPath = path_value.toLowerCase();
  const match = BUSINESS_UNITS.find((bu) => {
    const basePath = bu.basePath.toLowerCase();
    return normalizedPath === basePath || normalizedPath.startsWith(`${basePath}/`);
  });
  return match?.key;
}

export function getBuFromDomain(origin_value: string): BusinessUnitKey | undefined {
  const match = BUSINESS_UNITS.find((bu) => bu.domain && bu.domain === origin_value);
  return match?.key;
}

export function isBusinessUnitDomain(origin_value: string): boolean {
  return !!getBuFromDomain(origin_value);
}

export function normalizeBusinessUnit(
  value: string | BusinessUnitKey | null | undefined,
): BusinessUnitKey | undefined {
  if (!value) return undefined;
  const normalized = String(value).toUpperCase();
  const match = BUSINESS_UNITS.find((bu) => bu.key === normalized);
  return match?.key;
}

export function getStoredBu(): BusinessUnitKey | undefined {
  if (typeof window === 'undefined') return undefined;
  const value = window.localStorage.getItem(BU_STORAGE_KEY);
  if (!value) return undefined;

  return normalizeBusinessUnit(value);
}

export function setStoredBu(key_value: BusinessUnitKey) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(BU_STORAGE_KEY, key_value);
}

export function getEffectiveBu(options: {
  path_value: string;
  origin_value: string;
  use_stored?: boolean;
}) {
  // Path prefix always wins (explicit namespace).
  const byPath = getBuFromPath(options.path_value);
  if (byPath) return byPath;

  // Then production domain.
  const byDomain = getBuFromDomain(options.origin_value);
  if (byDomain) return byDomain;

  // Finally stored BU for staging/localhost shared routes.
  if (options.use_stored) return getStoredBu();

  return undefined;
}

export function getBuConfigForContext(options: {
  path_value: string;
  origin_value: string;
  use_stored?: boolean;
}) {
  const resolved_bu = getEffectiveBu(options);
  return getBusinessUnitConfig(resolved_bu);
}

// Centralized login resolution to avoid BU-specific branching in middlewares.
export function getLoginRouteNameForPath(options: {
  path_value: string;
  origin_value: string;
  use_stored?: boolean;
}) {
  const config = getBuConfigForContext(options);
  return config?.loginRouteName ?? DEFAULT_LOGIN_ROUTE_NAME;
}
